import './styles/ManageUsers.scss';

import { useQuery } from '@apollo/client';
import { Grid, Icon, Paper, Pagination } from '@mui/material';
import { ShibUser } from '@thro-types/users';

import { ChangeEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';

import QUERY from 'gql/queries/getUsers.gql';

import GQLGuard from 'app/components/GQLGuard';
import { buildListHeading, RichList } from 'app/components/RichList';
import { NoItems } from 'app/components/RichList/components/NoItems';
import { ShibUserItem } from 'app/components/RichList/items/Users/ShibUserItem';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import { UserType } from 'utils/ACLHelper';
import usePageHeading from 'utils/hooks/usePageHeading';

export function ShibUserList() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const uidFilter = useState('');
  const emailFilter = useState('');
  const nameFilter = useState('');
  const [elementsPerPage] = useState(10);

  const shibUserQuery = useQuery<
    any,
    {
      type?: string;
      application?: string;
      offset?: number;
      limit?: number;
    }
  >(QUERY, {
    fetchPolicy: 'no-cache',
    variables: { type: UserType.SHIBBOLETH, offset: 0, limit: elementsPerPage },
    context: { clientName: 'federation' }
  });
  const { refetch } = shibUserQuery;

  const handlePagination = (event: ChangeEvent<unknown>, page: number) => {
    const offset = (page - 1) * elementsPerPage;
    refetch({ offset: offset });
  };

  const filters = [
    {
      field: 'uid',
      label: t(translations.pages.shibUserList.uid()),
      stateHandler: uidFilter
    },
    {
      field: 'email',
      label: t(translations.common.email_short()),
      stateHandler: emailFilter
    },
    {
      field: 'name',
      label: t(translations.common.name()),
      stateHandler: nameFilter
    }
  ];
  const getListHeading = () => {
    return buildListHeading(
      [
        { weight: 1, title: t(translations.common.id()) },
        { weight: 3, title: t(translations.pages.shibUserList.uid()) },
        { weight: 3, title: t(translations.common.email()) },
        { weight: 2, title: t(translations.common.firstName()) },
        { weight: 2, title: t(translations.common.lastName()) },
        { weight: 1, title: t(translations.pages.shibUserList.actions()) }
      ],
      'users-management__list-heading__item'
    );
  };

  usePageHeading({
    heading: t(translations.layout.menu.shibUserManagement()),
    mobilePadding: false
  });

  return (
    <GQLGuard
      query={shibUserQuery}
      onError={error => {
        dispatch(
          AlertActions.error(`${t(translations.pages.shibUserList.alerts.loadUsersError())}`)
        );
        navigate('/', { replace: true });
        return null;
      }}
    >
      {(shibUserData: { users: ShibUser[]; usersCount: number }) => {
        let usersCount = shibUserData.usersCount;
        return (
          <Grid container className="users-management" component={Paper}>
            <RichList
              filters={filters}
              onFilterChange={refetch}
              staticHeading={
                <Grid container className="users-management__list-heading" component={Paper} square>
                  {getListHeading()}
                </Grid>
              }
            >
              {shibUserData.users.length === 0 ? (
                <NoItems icon={<Icon className="far fa-smile-beam" />} />
              ) : (
                <Grid container component={Paper} square>
                  {shibUserData.users.map(user => (
                    <ShibUserItem user={user} />
                  ))}
                </Grid>
              )}
            </RichList>
            {Math.floor(usersCount / elementsPerPage) > 0 && usersCount !== elementsPerPage && (
              <Grid container className="invoice-management__pagination">
                <Pagination
                  count={Math.ceil(usersCount / elementsPerPage)}
                  onChange={handlePagination}
                  defaultPage={1}
                  variant="outlined"
                  shape="rounded"
                  showFirstButton
                  showLastButton
                  size="medium"
                  color="primary"
                  siblingCount={2}
                  boundaryCount={2}
                />
              </Grid>
            )}
          </Grid>
        );
      }}
    </GQLGuard>
  );
}
